'use client'

import { createContext, useState } from 'react'

// 1) List out every “model” you support. Make sure this exactly matches the keys
//    you used before (e.g. 'Hyperreal', 'Anime', etc.).
const MODELS = [
  'all',
  'Hyperreal',
  'Anime',
  'Hyperanime',
  'Real',
  'K-Pop',
  'Fur',
  'Furtoon',
  'Anime XL',
  'Cinematic XL',
  'Hardcore XL',
  'Pony XL',
  'Pony Anime XL',
  'Pony Photo XL',
  'Pony Illustration XL',
  'Pony Render XL',
  'Pony XL 2',
  'Pony Photo XL 2',
  'Cinematic Flame',
]

// New: orientations, treated similarly to mediaType
const ORIENTATIONS = ['all', 'gay', 'straight', 'lesbian']

// 2) Trending's date ranges. Top has none — it is pinned to the last year and is not
//    user-switchable, so it needs no dateRange dimension in its cache.
export const TRENDING_DATE_RANGES = ['month', 'week', 'day']

// ─────────────────────────────────────────────────────────────────────────────
// Helpers for the "trending"/"top" cache shape:
//
// - Each leaf needs an object keyed by date range, e.g. { month: [], week: [], … }.
// - Then we wrap that under each model key,
// - then under each mediaType,
// - then under each orientation.
// ─────────────────────────────────────────────────────────────────────────────
function makePerDateRangeEmpty(ranges) {
  const obj = {}
  ranges.forEach((r) => {
    obj[r] = []
  })
  return obj
}

function makePerModelPerDateRangeEmpty(ranges) {
  const obj = {}
  MODELS.forEach((m) => {
    obj[m] = makePerDateRangeEmpty(ranges)
  })
  return obj
}

function makeDateRangeCacheStructure(ranges) {
  const byOrientation = {}
  ORIENTATIONS.forEach((orientation) => {
    byOrientation[orientation] = {
      all: makePerModelPerDateRangeEmpty(ranges),
      image: makePerModelPerDateRangeEmpty(ranges),
      video: makePerModelPerDateRangeEmpty(ranges),
    }
  })
  return byOrientation
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers for the “featured” cache shape (no dateRange dimension — it isn't a
// windowed feed; “top” moved to the keyed-by-range shape above):
//
// - Each leaf is just an array, so { all: [], Hyperreal: [], … } per mediaType,
// - wrapped per orientation.
// ─────────────────────────────────────────────────────────────────────────────
function makePerModelEmpty() {
  const obj = {}
  MODELS.forEach((m) => {
    obj[m] = []
  })
  return obj
}

function makeMediaTypeStructure() {
  return {
    all: makePerModelEmpty(),
    image: makePerModelEmpty(),
    video: makePerModelEmpty(),
  }
}

function makeOrientationMediaTypeStructure() {
  const obj = {}
  ORIENTATIONS.forEach((orientation) => {
    obj[orientation] = makeMediaTypeStructure()
  })
  return obj
}

// ─────────────────────────────────────────────────────────────────────────────
// Create the Context and Provider
// ─────────────────────────────────────────────────────────────────────────────
export const ExploreContext = createContext({
  topImagesByMediaType: null,
  setTopImagesByMediaType: () => {},
  trendingImagesByMediaType: null,
  setTrendingImagesByMediaType: () => {},
  featuredImagesByMediaType: null,
  setFeaturedImagesByMediaType: () => {},
})

export const ExploreProvider = ({ children }) => {
  // 1) “Top” (no dateRange): orientation → mediaType → model → []
  //    Top is pinned to the last year server-side and the window is not exposed to
  //    the user, so there is only ever one bucket to cache per combination.
  const [topImagesByMediaType, setTopImagesByMediaType] = useState(
    makeOrientationMediaTypeStructure(),
  )

  // 2) “Trending” (with dateRange):
  //    orientation → mediaType → model → { month, week, day }
  const [trendingImagesByMediaType, setTrendingImagesByMediaType] = useState(
    () => makeDateRangeCacheStructure(TRENDING_DATE_RANGES),
  )

  // 3) “Featured” (formerly “newest”) (no dateRange):
  //    orientation → mediaType → model → []
  const [featuredImagesByMediaType, setFeaturedImagesByMediaType] = useState(
    makeOrientationMediaTypeStructure(),
  )

  return (
    <ExploreContext.Provider
      value={{
        topImagesByMediaType,
        setTopImagesByMediaType,
        trendingImagesByMediaType,
        setTrendingImagesByMediaType,
        featuredImagesByMediaType,
        setFeaturedImagesByMediaType,
      }}
    >
      {children}
    </ExploreContext.Provider>
  )
}
